import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AtSign, ChevronRight } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEvents } from "@/store/events";
import { useWorkspace } from "@/store/workspace";
import type { WSEvent } from "@/lib/types";

interface Props {
  accountId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function NewChatDialog({ accountId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();
  const { selectChat } = useWorkspace();
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  // command_id отправленной команды — ждём по нему ответное событие от воркера.
  const pendingId = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      setUsername("");
      setLoading(false);
      pendingId.current = null;
    }
  }, [open]);

  // Резолв username идёт в воркере асинхронно — ждём chat.started / command.* по WS.
  useEffect(() => {
    if (!open) return;
    return useEvents.getState().subscribe(accountId, (e: WSEvent) => {
      const cid = (e.data as { command_id?: string }).command_id;
      if (!pendingId.current || cid !== pendingId.current) return;
      if (e.type === "chat.started") {
        const chatId = (e.data as { chat_id?: string }).chat_id;
        pendingId.current = null;
        setLoading(false);
        qc.invalidateQueries({ queryKey: ["chats", accountId] });
        if (chatId) selectChat(chatId);
        push({ title: "Чат открыт", variant: "success" });
        onOpenChange(false);
      } else if (e.type === "command.failed" || e.type === "command.rejected") {
        const d = e.data as { detail?: string; reason?: string };
        pendingId.current = null;
        setLoading(false);
        push({
          title: "Не удалось начать чат",
          description: d.detail ?? d.reason ?? "пользователь не найден",
          variant: "destructive",
        });
      }
    });
  }, [open, accountId, qc, selectChat, push, onOpenChange]);

  async function submit() {
    const u = username.trim().replace(/^@/, "");
    if (!u) return;
    setLoading(true);
    try {
      const r = await api.startChat(accountId, u);
      pendingId.current = r.command_id;
    } catch (e) {
      setLoading(false);
      push({
        title: "Ошибка",
        description: e instanceof ApiError ? e.message : String(e),
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Новый чат</DialogTitle>
          <DialogDescription>
            Введите @username пользователя. Воркер найдёт его и откроет диалог.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Username</Label>
            <div className="mt-1.5 flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-border bg-secondary px-3 text-muted-foreground">
                <AtSign className="h-4 w-4" />
              </span>
              <div className="flex-1 [&_input]:rounded-l-none">
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="durov"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                />
              </div>
            </div>
          </div>
          <Button onClick={submit} disabled={loading || !username.trim()} className="w-full">
            {loading ? "Ищу пользователя…" : "Начать чат"}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
